import { useCallback } from 'react';

import {
  IOnboardingJourneyFlowType,
  IOnboardingJourneyGlobalStatus,
  IUserOnboardingFlowStateEnum,
} from '@r-client/data/graphql-types';
import { getUserIntentFromRoute } from '@r-client/republic/util/route-intent';
import { useGqlClient } from '@r-client/shared/data/client';
import { toRelativeUrl } from '@r-client/shared/util/core';

import { GetOnboardingFlowStateQuery } from '../graphql/get-onboarding-flow-state-query';
import { GetOnboardingJourneyQuery } from '../graphql/get-onboarding-journey-query';
import {
  intentToFlowState,
  IS_INTENT_REDIRECTION_ENABLED,
  isOnboardingActive,
} from '../utils/onboarding-flow-state';
import { useUpdateOnboardingFlowState } from './use-update-onboarding-flow-state';

const ONBOARDING_PATH = '/user/onboarding';
const WELCOME_PATH = '/user/onboarding/welcome';

/**
 * Carries the user's original destination onto an onboarding URL, so the flow
 * lands there on completion.
 *
 * A plain login's destination is the (absolute) home URL, which relativizes to
 * `/` — the same place onboarding falls back to anyway, so carrying that would
 * be noise. Only a real destination is preserved.
 */
const withReturnTo = (url: string, relativeReturnTo?: string): string => {
  if (!relativeReturnTo || relativeReturnTo === '/') return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${new URLSearchParams({
    return_to: relativeReturnTo,
  })}`;
};

/**
 * Post-login check for US global-auth users: returns the onboarding URL
 * (carrying the original destination as `return_to`) when the user has an
 * unfinished onboarding journey, or null to proceed to the destination.
 *
 * `user.onboardingFlowState` decides *whether* to resume, so this runs on every
 * US login — including a plain one with no intent, which is the case a
 * URL-derived check can't cover. `Undecided` means the user reached the welcome
 * page but never picked a flow.
 *
 * Login intent is a flow-type *override*: a destination we can classify wins over
 * the stored flow, so a user who logs in heading somewhere specific gets the
 * onboarding for where they're going. While `IS_INTENT_REDIRECTION_ENABLED` is off,
 * such a login skips onboarding entirely instead.
 *
 * The journey status is only checked when resuming the *stored* flow, because the
 * stored flow is what created that journey. Checking it for an overriding intent
 * would ask about a journey that was never created, read the absence as "nothing to
 * resume", and clear the user's flow state. Entering a flow the user was not already
 * in needs no check: the flow-state write is what creates its journey.
 *
 * No onboarding-wide kill switch here on purpose: the state only ever becomes
 * non-`Inactive` via the post-registration path, which is already gated, so with
 * onboarding disabled every user reads `Inactive` and falls straight through.
 */
export const usePostLoginOnboardingRedirect = () => {
  const client = useGqlClient();
  const updateFlowState = useUpdateOnboardingFlowState();

  return useCallback(
    async (returnTo: string): Promise<string | null> => {
      const relativeReturnTo = toRelativeUrl(returnTo);

      // The onboarding page parses `flow_type` case-insensitively back into the same
      // enum, so derive it instead of duplicating the value.
      const investmentOnboardingUrl = () =>
        withReturnTo(
          `${ONBOARDING_PATH}?${new URLSearchParams({
            flow_type: IOnboardingJourneyFlowType.Investment.toLowerCase(),
          })}`,
          relativeReturnTo
        );

      // A classifiable destination wins over the stored flow.
      const intentFlowState = intentToFlowState(
        getUserIntentFromRoute(relativeReturnTo)
      );

      // Heading somewhere with an intent goes straight there for now. Nothing is
      // read or written, so the stored flow still resumes on a later plain login.
      if (!IS_INTENT_REDIRECTION_ENABLED && intentFlowState) return null;

      try {
        const flowStateResult = await new GetOnboardingFlowStateQuery({
          client,
          lazy: true,
          // A stale read here would resume (or skip) onboarding wrongly.
          fetchPolicy: 'network-only',
        }).load();

        const storedFlowState =
          flowStateResult?.data?.user?.onboardingFlowState;

        if (!isOnboardingActive(storedFlowState)) return null;

        if (storedFlowState !== IUserOnboardingFlowStateEnum.Investment) {
          // No investment journey to check: `Undecided` never picked a flow, and a
          // stored trading flow can't be resumed or evaluated yet.
          // TODO: https://linear.app/republic/issue/USMKT-2195/fe-enable-redirection-to-trading-onboarding-when-trading-flow-is-ready
          // An invest intent starts the investment flow instead, and this write is
          // what creates its journey — so it needs no status check.
          if (intentFlowState === IUserOnboardingFlowStateEnum.Investment) {
            await updateFlowState(intentFlowState);
            return investmentOnboardingUrl();
          }

          // Never picked a flow and none derivable — let them pick again. A stored
          // trading flow is left alone: it's what will resume it once that flow
          // exists (USMKT-2195).
          return storedFlowState === IUserOnboardingFlowStateEnum.Undecided
            ? withReturnTo(WELCOME_PATH, relativeReturnTo)
            : null;
        }

        // TODO: https://linear.app/republic/issue/USMKT-2195/fe-enable-redirection-to-trading-onboarding-when-trading-flow-is-ready
        // A trade intent can't override into onboarding that doesn't exist yet.
        // Leave the stored investment flow alone and proceed.
        if (intentFlowState === IUserOnboardingFlowStateEnum.Trading)
          return null;

        // Resuming the stored investment flow, so its journey exists and the status
        // is meaningful.
        const journeyResult = await new GetOnboardingJourneyQuery({
          client,
          lazy: true,
          fetchPolicy: 'network-only',
          variables: { flowType: IOnboardingJourneyFlowType.Investment },
        }).load();

        const status = journeyResult?.data?.onboardingJourney?.globalStatus;
        if (
          status === IOnboardingJourneyGlobalStatus.NotStarted ||
          status === IOnboardingJourneyGlobalStatus.InProgress
        ) {
          // TODO: https://linear.app/republic/issue/USMKT-2195/fe-enable-redirection-to-trading-onboarding-when-trading-flow-is-ready
          // This is where the chart's gated write-back belongs: an intent that
          // differs from the stored flow gets written here, once the journey has
          // confirmed there is something to resume. Today the only intent that can
          // differ from a stored `Investment` is `Trading`, which returned above, so
          // there is never anything to write.
          // https://www.figma.com/board/pY8Ul4uNGFZzEzEuGntJ4r/My-Flow-charts?node-id=106-987&t=X9TPva0rWZwwM4Nf-4
          return investmentOnboardingUrl();
        }

        // Nothing left to do — the steps were completed outside the funnel.
        // Awaited so the write is in flight before the caller navigates.
        await updateFlowState(IUserOnboardingFlowStateEnum.Inactive);
        return null;
      } catch {
        // never block login on the onboarding check
        return null;
      }
    },
    [client, updateFlowState]
  );
};
